import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CardHeader,
    CardContent,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { setModal } from '../../../../Common/App';
import { useDispatch } from 'react-redux';
import { DialogCard, Loader } from '../../../../../Components';
import { fetchUsers } from '../../../../Core/Users';
import { fetchRosterEntries } from '../../../../Roster/Roster';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    backgroundColor: theme.palette.background.paper,
    '& .MuiTableCell-root': {
        padding: theme.spacing(1.5, 2),
        fontSize: '0.875rem',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        lineHeight: 1.5
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'none',
        letterSpacing: '0.01em',
        fontSize: '0.8125rem',
        borderBottom: `2px solid ${theme.palette.grey[200]}`
    },
    '& .MuiTableRow-root:hover': {
        backgroundColor: theme.palette.grey[50],
        transition: 'background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
    }
}));

export const StaffListModal = ({ department, weekRange = {} }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [rosterEntries, setRosterEntries] = useState([]);
    
    const hasRosterThisWeek = useCallback((userId) => {
        return rosterEntries.some(entry => entry.user_id === userId);
    }, [rosterEntries]);

    const sortedStaffList = useMemo(() => {
        return [...staffList].sort((a, b) => {
            const aScheduled = hasRosterThisWeek(a.id) ? 1 : 0;
            const bScheduled = hasRosterThisWeek(b.id) ? 1 : 0;
            return bScheduled - aScheduled;
        });
    }, [staffList, hasRosterThisWeek]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const departmentId = department?.id || '';
        const payload = {
            paginate: false,
            department_id: departmentId,
            columns: ['id', 'first_name', 'last_name', 'mobile', 'designation', 'event', 'avatar']
        };
        
        await fetchUsers({ dispatch, filters: payload }).then((res) => {
            setIsLoading(false);
            if (res.success) {
                setStaffList(res.data || []);
            }
        });

        await fetchRosterEntries({ dispatch, filters: { department_id: departmentId, start_date: weekRange.startDate, end_date: weekRange.endDate, paginate: false } }).then((res) => {
            setIsLoading(false);
            if (res.success) {
                setRosterEntries(res.data || []);
            }
        });
    }, [dispatch, department?.id, weekRange.startDate, weekRange.endDate]);

    useEffect(() => {
        if (department?.id) {
            fetchData();
        }
    }, [fetchData, department?.id]);

    return (
        <DialogCard>
            <CardHeader
                title={`${department?.department_name || 'Department'} Staff`}
                subheader={`Scheduled Staff: ${department?.scheduled_staff_count || 0}`}
                avatar={<Loader isLoading={isLoading} type="linear" />}
                action={
                    <IconButton
                        aria-label="Close"
                        size="small"
                        onClick={() => dispatch(setModal({ component: null, open: false }))}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
            <CardContent
                sx={{
                    p: `0!important`,
                    ...(isMobile && {
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    })
                }}
            >
                {staffList.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No staff members found in this department
                        </Typography>
                    </Box>
                ) : isMobile ? (
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1 }}>
                        {sortedStaffList.map((staff) => (
                            <Box
                                key={staff.id}
                                sx={{
                                    p: 1.5,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    backgroundColor: 'background.paper'
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {staff.first_name} {staff.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {staff.designation || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} color="text.secondary">
                                            {staff.mobile || 'N/A'}
                                        </Typography>
                                    </Box>
                                    {hasRosterThisWeek(staff.id) ? (
                                        <Chip label="Scheduled" color="success" size="small" sx={{ fontWeight: 500, fontSize: '0.75rem' }} />
                                    ) : (
                                        <Chip label="Not Scheduled" variant="outlined" size="small" sx={{ fontWeight: 500, fontSize: '0.75rem' }} />
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <StyledTableContainer>
                        <Table size="medium" sx={{ minWidth: 320 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">Name</TableCell>
                                    <TableCell align="left">Designation</TableCell>
                                    <TableCell align="center">Contact</TableCell>
                                    <TableCell align="center">This Week</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedStaffList.map((staff) => (
                                    <TableRow key={staff.id} hover>
                                        <TableCell align="left">
                                            <Typography variant="body2" fontWeight={500}>
                                                {staff.first_name} {staff.last_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Typography variant="body2" color="text.secondary">
                                                {staff.designation || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                                                {staff.mobile || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasRosterThisWeek(staff.id) ? (
                                                <Chip 
                                                    label="Scheduled" 
                                                    color="success" 
                                                    size="small"
                                                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                                                />
                                            ) : (
                                                <Chip 
                                                    label="Not Scheduled" 
                                                    variant="outlined" 
                                                    size="small"
                                                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                )}
            </CardContent>
        </DialogCard>
    );
};

